/**
 * Top-level ErrorBoundary — без неё любой throw в render роняет весь
 * react tree, и навигация после этого показывает «чёрный экран».
 *
 * При ошибке: показывает плашку с кратким текстом + кнопка Reload.
 * В консоль выводит детали для диагностики.
 *
 * React requires the boundary itself to be a class component (no
 * functional equivalent for componentDidCatch / getDerivedStateFromError
 * as of React 19). User-facing strings live in `ErrorBoundaryFallback`,
 * a sibling functional component that pulls them via useTranslation so
 * the UI stays in the user's locale instead of hardcoded Russian.
 */
import { Component, type ReactNode, type ErrorInfo } from "react";
import { useTranslation } from "react-i18next";
import { Warning } from "@phosphor-icons/react";

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  message: string | null;
};

// H4 — best-effort Sentry forwarding. The boundary stays tree-shake
// safe (no static @sentry/react import); main.tsx attaches the SDK to
// window.__sentry only when VITE_SENTRY_DSN is set.
type SentryLike = {
  captureException: (err: unknown, ctx?: unknown) => void;
};
function getSentry(): SentryLike | null {
  try {
    const s = (window as unknown as { __sentry?: SentryLike }).__sentry;
    if (s && typeof s.captureException === "function") return s;
  } catch {
    // silent: don't escalate sentry's own failure
  }
  return null;
}

function ErrorBoundaryFallback({
  message,
  onReset,
  onReload,
}: {
  message: string | null;
  onReset: () => void;
  onReload: () => void;
}) {
  const { t } = useTranslation("common");
  return (
    <div className="min-h-screen bg-bg-base text-fg-primary flex items-center justify-center p-6">
      <div className="max-w-sm w-full space-y-4 text-center">
        <div className="flex justify-center">
          <Warning className="w-12 h-12 text-amber-300" />
        </div>
        <h1 className="text-lg font-semibold">{t("error_boundary.title")}</h1>
        <p className="text-sm text-fg-secondary leading-relaxed">
          {t("error_boundary.description")}
        </p>
        {message && (
          <details className="text-[11px] text-fg-muted text-left bg-bg-raised rounded-pill p-2 border border-bg-border">
            <summary className="cursor-pointer">
              {t("error_boundary.details_summary")}
            </summary>
            <pre className="mt-1 whitespace-pre-wrap break-words">{message}</pre>
          </details>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onReset}
            className="flex-1 px-3 py-2 rounded-pill bg-bg-raised border border-bg-border text-fg-primary text-sm hover:border-accent/40 transition-colors"
          >
            {t("error_boundary.btn_retry")}
          </button>
          <button
            type="button"
            onClick={onReload}
            className="flex-1 px-3 py-2 rounded-pill bg-accent text-accent-on font-semibold text-sm hover:bg-accent-hover transition-colors"
          >
            {t("error_boundary.btn_reload")}
          </button>
        </div>
      </div>
    </div>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, message: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error?.message ?? "unknown error" };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
    const sentry = getSentry();
    if (sentry) {
      try {
        sentry.captureException(error, {
          contexts: { react: { componentStack: info?.componentStack } },
        });
      } catch {
        // silent: don't escalate sentry's own failure
      }
    }
  }

  reset = () => {
    this.setState({ hasError: false, message: null });
  };

  reload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorBoundaryFallback
          message={this.state.message}
          onReset={this.reset}
          onReload={this.reload}
        />
      );
    }
    return this.props.children;
  }
}
